import React from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/shared/PageHeader';
import SEOHead, { getBreadcrumbSchema } from '@/components/shared/SEOHead';
import CTASection from '@/components/home/CTASection';
import { services } from '@/data/mock';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Building2, Layers, HardHat, Mountain, Pipette, PenTool, Route, Droplets, MapPin, TestTube, Box, ArrowRight } from 'lucide-react';

const iconMap = {
  Building2,
  Layers,
  HardHat,
  Mountain,
  Pipette,
  PenTool,
  Route,
  Droplets,
  MapPin,
  TestTube,
  Box,
};

const ServicesPage = () => {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://redspire.com' },
    { name: 'Services', url: 'https://redspire.com/services' }
  ]);

  return (
    <Layout>
      <SEOHead
        title="Civil Engineering Services"
        description="Comprehensive civil engineering services including infrastructure development, structural engineering, construction management, and more. Serving Connecticut and the Northeast."
        keywords={['civil engineering services', 'infrastructure development', 'structural engineering', 'construction management', 'Connecticut engineering']}
        schema={breadcrumbSchema}
      />

      <PageHeader
        title="Our Services"
        subtitle="Comprehensive civil engineering solutions from concept to completion."
        breadcrumb="Services"
        backgroundImage="https://images.unsplash.com/photo-1485083269755-a7b559a4fe5e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Full Service Portfolio</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">Engineering Excellence Across Disciplines</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              From infrastructure development to digital twin engineering, Redspire delivers comprehensive civil engineering 
              services with technical precision and innovative solutions. Click any service to learn more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Building2;
              return (
                <Link key={service.id} to={service.href}>
                  <Card
                    className="group bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-red-100 transition-all duration-300 overflow-hidden h-full"
                  >
                    <CardContent className="p-8">
                      <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors duration-300">
                        <IconComponent className="w-8 h-8 text-red-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-red-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <span className="inline-flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all duration-300">
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Approach</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">How We Deliver</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your needs, constraints, and project goals.' },
              { step: '02', title: 'Design', description: 'Engineering solutions with precision and innovation.' },
              { step: '03', title: 'Delivery', description: 'Executing with safety-first practices and quality control.' },
              { step: '04', title: 'Support', description: 'Ongoing maintenance and lifecycle management services.' },
            ].map((phase, index) => (
              <div key={index} className="text-center relative">
                <div className="w-16 h-16 rounded-full bg-red-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {phase.step}
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-red-200" />
                )}
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{phase.title}</h3>
                <p className="text-gray-600">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </Layout>
  );
};

export default ServicesPage;
